/**
 * DirectionEntity - 방향 도형 Entity
 */
import { SHAPES, DIR_DRAW_ARROW_SIZE } from '../../../const'
import type { ICanvasEngineEntityController } from '../../../types/interface'
import type { Vector2d } from '../../../types'
import type { ICircle, IGroup, IRenderer } from '../../../backend/IRenderer'

/**
 * Direction 생성 설정
 */
export interface DirectionCreateConfig {
  color: string
}

/**
 * Direction 초기화 설정
 */
export interface DirectionInitConfig {
  id?: string
  createPoint: Vector2d
  balloonPoint?: Vector2d
  config: DirectionCreateConfig
  points?: number[]
  layerId?: string
  balloonFontColor?: string
  balloonFillColor?: string
  balloonScale?: number
  connectedLineThicknessScale?: number
  balloonFontScale?: number
  pointShapeScale?: number
  areaBorderThicknessScale?: number
  interactionMode?: boolean
}

/**
 * Direction 도형 생성 함수
 * @param canvasEngine - CanvasEngine 컨트롤러
 * @param initConfig - 초기화 설정
 * @returns Direction 관련 노드 핸들
 */
export function createDirectionShape(
  renderer: IRenderer,
  canvasEngine: ICanvasEngineEntityController,
  initConfig: DirectionInitConfig,
): {
  directionShape: IGroup
  centerPoint: ICircle
  directionPoints: number[]
  color: string
} {
  const { config, points } = initConfig
  const color = config.color
  const strokeWidth = canvasEngine.areaLineWidth

  // 그룹 생성 (절대좌표 사용)
  const directionGroup = renderer.createGroup({ x: 0, y: 0, listening: false })

  // 이동 및 선택을 위한 투명한 원 추가 (핸들용, 2배 확대)
  const centerPoint = renderer.createCircle({
    x: points ? points[0] : 0,
    y: points ? points[1] : 0,
    radius: 20, // 2배 확대 (기존 10에서 20으로)
    opacity: 0,
  })

  // 초기 포인트 설정
  const directionPoints: number[] = points ? [...points] : []

  if (points && points.length >= 4) {
    // 화살표 머리 생성
    const startX = points[0]
    const startY = points[1]
    const secondX = points[2]
    const secondY = points[3]

    const dx = secondX - startX
    const dy = secondY - startY
    const length = Math.sqrt(dx * dx + dy * dy)

    if (length > 0) {
      const dirX = dx / length
      const dirY = dy / length

      const arrowEndX = startX + dirX * DIR_DRAW_ARROW_SIZE
      const arrowEndY = startY + dirY * DIR_DRAW_ARROW_SIZE

      const arrowHead = renderer.createArrow({
        points: [arrowEndX, arrowEndY, startX, startY],
        pointerLength: DIR_DRAW_ARROW_SIZE,
        pointerWidth: DIR_DRAW_ARROW_SIZE,
        fill: color,
        stroke: color,
        strokeWidth: 1,
        listening: false,
      })
      directionGroup.add(arrowHead)
    }

    // 꺾인 선분들 생성
    for (let i = 0; i < points.length - 2; i += 2) {
      let segStartX = points[i]
      let segStartY = points[i + 1]
      const endX = points[i + 2]
      const endY = points[i + 3]

      // 첫 번째 선분의 경우 화살표 머리와 겹치지 않도록 시작점을 앞으로 이동
      if (i === 0) {
        const segDx = endX - segStartX
        const segDy = endY - segStartY
        const segLength = Math.sqrt(segDx * segDx + segDy * segDy)

        if (segLength > DIR_DRAW_ARROW_SIZE) {
          const dirX = segDx / segLength
          const dirY = segDy / segLength
          segStartX = segStartX + dirX * DIR_DRAW_ARROW_SIZE
          segStartY = segStartY + dirY * DIR_DRAW_ARROW_SIZE
        }
      }

      const line = renderer.createLine({
        points: [segStartX, segStartY, endX, endY],
        stroke: color,
        strokeWidth: strokeWidth,
        lineCap: 'square',
        listening: false,
      })
      directionGroup.add(line)
    }
  }

  return {
    directionShape: directionGroup,
    centerPoint,
    directionPoints,
    color,
  }
}

/**
 * Direction Entity export 데이터
 */
export interface DirectionExportData {
  type: typeof SHAPES.DIRECTION
  shapeId: string
  point: Vector2d
  balloonPoint: Vector2d
  color: string
  layerId: string
  points?: number[]
  balloonFontColor?: string
  balloonFillColor?: string
  balloonScale?: number
  connectedLineThicknessScale?: number
  balloonFontScale?: number
  pointShapeScale?: number
  areaBorderThicknessScale?: number
}

/**
 * Direction Entity를 export 데이터로 변환
 * @param entity - Entity 정보
 * @returns Export 데이터
 */
export function exportDirectionData(entity: {
  id: string
  position: Vector2d
  balloonPosition: Vector2d
  color: string
  layerId: string
  points?: number[]
  balloonFontColor?: string
  balloonFillColor?: string
  balloonScale?: number
  connectedLineThicknessScale?: number
  balloonFontScale?: number
  pointShapeScale?: number
  areaBorderThicknessScale?: number
}): DirectionExportData {
  // points를 상대좌표로 변환
  const relativePoints = entity.points?.map((p, i) =>
    i % 2 === 0 ? p - entity.position.x : p - entity.position.y,
  )

  return {
    type: SHAPES.DIRECTION,
    shapeId: entity.id,
    point: entity.position,
    balloonPoint: entity.balloonPosition,
    color: entity.color,
    layerId: entity.layerId,
    points: relativePoints,
    balloonFontColor: entity.balloonFontColor,
    balloonFillColor: entity.balloonFillColor,
    balloonScale: entity.balloonScale,
    connectedLineThicknessScale: entity.connectedLineThicknessScale,
    balloonFontScale: entity.balloonFontScale,
    pointShapeScale: entity.pointShapeScale,
    areaBorderThicknessScale: entity.areaBorderThicknessScale,
  }
}
